import { Button, LoadingDots, Sidebar } from '@flos/react-ui';
import { paymentFrequencyMap, periodicityUnit } from '../../utils/currencyUtil';
import CustomerBenefit from '../../shared_components/CustomerBenefit/CustomerBenefit';
import TotalPrice from '../../shared_components/TotalPrice/TotalPrice';
import ShoppingBasketSummaryCoverableList from '../ShoppingBasketSummaryPage/components/ShoppingBasketSummaryCoverableList';
import { getSortedByCoverablePrice } from '../ShoppingBasketPage/utils/ShoppingBasketUtil';
import { useHippoText } from '../../hooks/useHippoText';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { onsaProgressAtom } from '../../recoil/atoms/onsaProgressAtom';
import { onsaProductStatusListAtom } from '../../recoil/atoms/onsaProductStatusListAtom';
import { type ProductStatus } from '../../recoil/types/onsaTypes';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import {
    allCoverablesOnsaToCoverableDTOArray,
    allCoverablesToCoverableDTOArray
} from '../../utils/coverablesUtil';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { shoppingBasketOverlayAtom } from '../../recoil/atoms/shoppingBasketOverlayAtom';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { refreshCoverablesSelectorAtom } from '../../recoil/atoms/refreshCoverablesSelectorAtom';
import { onsaInfoAtom } from '../../recoil/atoms/onsaInfoAtom';
import { shouldPrefillNameOfInsuredAtom } from '../../recoil/atoms/shouldPrefillNameOfInsuredAtom';
import { Suspense } from 'react';

const ShoppingBasketOverlay = (): React.JSX.Element => {
    const getHippoText = useHippoText();
    const jobData = useRecoilValue(jobAtom);
    const setOnsaInfo = useSetRecoilState(onsaInfoAtom);
    const { wizardGotoStep } = useWizardNavigation();

    const [shoppingBasketOverlayInfo, setShoppingBasketOverlayInfo] =
        useRecoilState(shoppingBasketOverlayAtom);

    const lastProductButtonText = getHippoText('DSS-SHOPPING-BASKET-OVERLAY-GO-TO-BASKET');
    const uncalculatedProductsButtonText = getHippoText(
        'DSS-SHOPPING-BASKET-OVERLAY-CALCULATE-THE-INSURANCE'
    );
    const emptyBasketText = getHippoText('DSS-SHOPPING-BASKET-OVERLAY-EMPTY-BASKET-LABEL');
    const emptyBasketbuttonText = getHippoText('DSS-SHOPPING-BASKET-OVERLAY-EMPTY-BASKET-CONTINUE');

    const shoppingBasketCoverables = allCoverablesToCoverableDTOArray(
        useRecoilValue(allCoverablesAtom)
    );
    const onsaCoverables = allCoverablesOnsaToCoverableDTOArray(
        useRecoilValue(allCoverablesOnsaAtom)
    );
    const { sortedCoverables } = getSortedByCoverablePrice(
        shoppingBasketCoverables.concat(onsaCoverables)
    );
    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const paymentFrequencyPostfix = periodicityUnit[selectedPaymentFrequency];

    const isShoppingBasketEmpty = sortedCoverables.length === 0;

    const [productStatusList, setProductStatusList] = useRecoilState(onsaProductStatusListAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const resetOnsaProgress = useResetRecoilState(onsaProgressAtom);
    const resetOnsaCoverables = useSetRecoilState(allCoverablesOnsaAtom);
    const resetCoverables = useResetRecoilState(allCoverablesAtom);
    const setRefreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);
    const setShouldPrefillNameOfInsured = useSetRecoilState(shouldPrefillNameOfInsuredAtom);

    const updateProductStatusList = (): Record<string, ProductStatus[]> => {
        const updatedList = { ...productStatusList };

        for (const flavour in updatedList) {
            updatedList[flavour] = updatedList[flavour].map((cov: ProductStatus) => {
                if (cov.fixedId === currentProduct.fixedId) {
                    return { ...cov, calculated: true };
                }
                return cov;
            });
        }
        setProductStatusList(updatedList);

        return updatedList;
    };

    const isLastProduct = Object.values(productStatusList).every((flavour) =>
        flavour.every(
            (cov) =>
                cov.calculated ||
                (cov.fixedId === currentProduct.fixedId && cov.lobCode === currentProduct.lobCode)
        )
    );

    const totalPrice = sortedCoverables
        .map((coverable) => {
            return selectedPaymentFrequency === paymentFrequencyMap.monthly
                ? coverable.monthlyPrice?.amount ?? 0
                : coverable.yearlyPrice?.amount ?? 0;
        })
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

    const goNext = async (): Promise<void> => {
        if (shoppingBasketOverlayInfo.beforeGoNext) await shoppingBasketOverlayInfo.beforeGoNext();
        setShoppingBasketOverlayInfo({ isButtonDisplayed: false, isOverlayOpen: false });
        const updatedList = updateProductStatusList();
        const hasUncalculatedProduct = Object.values(updatedList).some((flavour) =>
            flavour.some((cov) => !cov.calculated)
        );
        setShouldPrefillNameOfInsured(false);
        resetOnsaProgress();
        if (hasUncalculatedProduct) {
            wizardGotoStep('CoverableInformationPage');
        } else {
            resetOnsaCoverables({});
            resetCoverables();
            setProductStatusList({});
            setOnsaInfo((prev) => {
                return { ...prev, onsaStep: undefined };
            });
            setRefreshCoverablesSelector({});
            wizardGotoStep('ShoppingBasketPage');
        }
    };

    return (
        <Sidebar
            isOpen={shoppingBasketOverlayInfo.isOverlayOpen}
            onClickOutside={() => {
                if (!isShoppingBasketEmpty) {
                    setShoppingBasketOverlayInfo({
                        isButtonDisplayed: false,
                        isOverlayOpen: false
                    });
                }
            }}
        >
            <p className="paragraph--small">{getHippoText('DSS-SHOPPING-BASKET-OVERLAY-HEADER')}</p>
            {isShoppingBasketEmpty ? (
                <div className="u-spacing-inset-xxl u-flex-box u-flex-box--col u-flex-box--center u-text-center">
                    <img
                        alt=""
                        // eslint-disable-next-line max-len
                        src="https://flos.topdanmark.cloud/development/static/media/streamlinehq-zero-purchase-2-400-ico_FUJuuvkmowZ1ZaFi.70598106.png"
                    />
                    <p className="h4 u-color-secondary-4-medium u-spacing-stack-none">
                        {emptyBasketText}
                    </p>
                    <Button
                        onClick={() => {
                            wizardGotoStep('ProductSelectorPage');
                            resetOnsaProgress();
                            setShoppingBasketOverlayInfo({
                                isButtonDisplayed: false,
                                isOverlayOpen: false
                            });
                        }}
                    >
                        {emptyBasketbuttonText}
                    </Button>
                </div>
            ) : (
                <div style={{ position: 'relative' }}>
                    <ShoppingBasketSummaryCoverableList
                        coverables={sortedCoverables ?? []}
                        paymentFrequencyPostfix={paymentFrequencyPostfix}
                        paymentFrequency={selectedPaymentFrequency}
                        shouldRenderOverlayDesign={true}
                    />
                    <div className="u-spacing-stack-xxl">
                        <Suspense fallback={<LoadingDots centered={false} />}>
                            <CustomerBenefit />
                        </Suspense>
                    </div>

                    <div
                        className="u-border-top-gradient-m--white u-bg-white"
                        style={{
                            position: 'sticky',
                            bottom: '-32px'
                        }}
                    >
                        <div className="u-border-primary-1-base--top">
                            <TotalPrice
                                totalPrice={totalPrice}
                                showYearlyPriceField={
                                    selectedPaymentFrequency === paymentFrequencyMap.monthly
                                }
                                paymentFrequencyPostfix={paymentFrequencyPostfix}
                                displayTaxesSubtitle
                            />
                        </div>
                        {shoppingBasketOverlayInfo.isButtonDisplayed && (
                            <div className="u-spacing-inset-m--stretch-max">
                                <Button
                                    onClick={() => {
                                        void goNext();
                                    }}
                                >
                                    {isLastProduct
                                        ? lastProductButtonText
                                        : uncalculatedProductsButtonText}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Sidebar>
    );
};

export default ShoppingBasketOverlay;
